import { setGlobalTheme } from '@atlaskit/tokens';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { hooks } from '@atlassian/mpac-utils';
import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Theme = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

type ThemeContextType = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

type ThemeProviderProps = {
  children: ReactNode;
};

const useSystemTheme = (): ResolvedTheme => {
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => {
    if (__REACT_SSR__) return 'light';
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return systemTheme;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { enableMpacWebCoherence } = useStatsig();
  const systemTheme = useSystemTheme();

  // Check for URL parameter for backward compatibility
  const urlTheme =
    typeof window !== 'undefined' && window.location.search.includes('theme=dark')
      ? 'dark'
      : 'light';

  // Use localStorage to persist theme preference, defaulting to URL parameter or 'light'
  const { state: theme, writeState: setThemeInStorage } = hooks.useLocalStorage<Theme>(
    'marketplace-theme-preference',
    urlTheme
  );

  // Ensure theme is valid
  const currentTheme: Theme = (['light', 'dark', 'auto'] as const).includes(theme as Theme)
    ? (theme as Theme)
    : 'light';

  // Resolve the actual theme to apply
  const resolvedTheme: ResolvedTheme = currentTheme === 'auto' ? systemTheme : currentTheme;

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeInStorage(newTheme);
    },
    [setThemeInStorage]
  );

  // Apply theme whenever it changes
  useEffect(() => {
    setGlobalTheme({
      colorMode: resolvedTheme,
      light: 'light',
      spacing: 'spacing',
      shape: 'shape',
      typography: enableMpacWebCoherence() ? 'typography' : 'typography-adg3',
    });
  }, [resolvedTheme, enableMpacWebCoherence]);

  const contextValue: ThemeContextType = useMemo(
    () => ({
      theme: currentTheme,
      resolvedTheme,
      setTheme,
    }),
    [currentTheme, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
